import { useCallback } from 'react';
import type { AttachmentInput } from 'sr-types/lib/attachment/v1/graphql';
import type { EntityName } from '../../auth/Security';
import { FETCH_POLICY_CONFIG } from '../../constants/constants';
import { useMutationWithContext } from '../../hooks/useMutationWithContext';
import { client } from '../attachmentsApi';
import { filteredAttachments } from '../attachmentUtils';
import { SaveAttachmentMutationError, SaveAttachmentServerError } from '../constants';
import { saveAttachmentsMutation } from './attachmentGraphqlApi';

export const useSaveAttachment = () => {
    const [save, { loading: isSaving }] = useMutationWithContext(saveAttachmentsMutation, client);

    const attachmentSave = useCallback(
        async (entityName: EntityName, entityId: string, input: AttachmentInput[], contextTerm?: string) => {
            const result = await save({
                variables: {
                    entityId,
                    entityType: entityName,
                    contextTerm,
                    input: filteredAttachments(input)
                },
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                errorPolicy: 'all'
            });

            if (result.errors?.length) {
                throw new SaveAttachmentMutationError(result.errors[0].message);
            }

            const errors =
                result.data?.saveAttachments?.flatMap((mutationResult) => mutationResult?.errors ?? []) ?? [];
            if (errors.length) {
                throw new SaveAttachmentServerError(errors[0]?.message ?? errors[0]?.key);
            }

            return result;
        },
        [save]
    );

    return { attachmentSave, isSaving };
};
